import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/landing/button";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { FadeUp } from "@/components/ui/animated";
import { JsonLd } from "@/components/landing/json-ld";
import { Link, Arrow } from "@/components/landing/link";
import { prepareQuizStart } from "../../analytics";
import type { Variant } from "../../experiments";
import { cn } from "@/lib/utils";

const faqs = [
  {
    value: "tracks",
    question: "What does Vessa track?",
    answer:
      "Prescribed plans, scheduled events, actual dose events, check-ins, symptoms, measurements, and supplies, kept as distinct records.",
  },
  {
    value: "dose",
    question: "Does Vessa recommend a dose?",
    answer:
      "No. Vessa records and explains context, but never tells you whether, when, or how much medication to take.",
  },
  {
    value: "storage",
    question: "Where is my health history stored?",
    answer:
      "App-owned records stay on your iPhone. The web quiz keeps answers only in the open tab and discards them on refresh or close.",
  },
  {
    value: "analytics",
    question: "What does website analytics receive?",
    answer:
      "Only session-scoped marketing events such as landing views and coarse funnel progress. Quiz answers and health values are excluded by a closed payload allowlist.",
  },
  {
    value: "appointment",
    question: "Can I prepare for an appointment?",
    answer:
      "Yes. Vessa can build a readable report from the records you choose, with plans and actual events clearly distinguished.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
};

function AccordionItemFAQs(props: React.ComponentProps<typeof AccordionItem>) {
  return (
    <AccordionItem
      {...props}
      className={cn(
        "bg-foreground/[0.04] data-[state=open]:bg-card data-[state=open]:border-border rounded-2xl border border-transparent px-5 py-2 transition-colors data-[state=open]:shadow-sm lg:px-7",
        props.className,
      )}
    />
  );
}

function AccordionTriggerFAQs(props: React.ComponentProps<typeof AccordionTrigger>) {
  return (
    <AccordionTrigger
      {...props}
      className={cn("[&[data-state=open]>svg]:text-foreground text-base lg:text-lg", props.className)}
    />
  );
}

function AccordionContentFAQs(props: React.ComponentProps<typeof AccordionContent>) {
  return <AccordionContent {...props} className={cn("text-muted-foreground lg:text-base", props.className)} />;
}

export function FAQs({ variant }: { variant: Variant<"landing-message"> }) {
  return (
    <div id="faq" className="mx-auto grid max-w-6xl gap-6 px-6 py-14 md:grid-cols-2 md:gap-14 md:px-10 md:py-25">
      <JsonLd data={faqSchema} />
      <FadeUp className="flex w-full flex-col gap-6">
        <Badge variant="secondary" className="mb-2 text-muted-foreground uppercase">
          FAQ
        </Badge>
        <h2 className="text-3xl leading-[1.1] font-medium tracking-tight sm:text-5xl">
          Frequently
          <br />
          Asked <span className="text-muted-foreground">Questions</span>
        </h2>
        <p className="max-w-lg text-xs leading-6 tracking-tight sm:text-base">
          Clear answers about what Vessa does and where treatment decisions belong.
        </p>
        <Button className="w-fit" size="lg" asChild>
          <Link
            href="/quiz"
            onClick={() =>
              prepareQuizStart({ placement: "faq", experiment: "landing-message", variant })
            }
          >
            Build my journal <Arrow />
          </Link>
        </Button>
      </FadeUp>
      <FadeUp delay={0.15}>
        <Accordion type="single" collapsible defaultValue="tracks" className="grid w-full gap-4">
          {faqs.map((faq) => (
            <AccordionItemFAQs key={faq.value} value={faq.value}>
              <AccordionTriggerFAQs>{faq.question}</AccordionTriggerFAQs>
              <AccordionContentFAQs>
                <p>{faq.answer}</p>
              </AccordionContentFAQs>
            </AccordionItemFAQs>
          ))}
        </Accordion>
      </FadeUp>
    </div>
  );
}
